import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { legacyAppUrl } from '../utils/legacy-app-url';
import { TOKEN_LOGIN_PATH } from './session-expiry.service';
import { UserService } from './user.service';
/** Backend path that expires the current session token server-side. */
const TOKEN_EXPIRE_PATH = '/token/token-expire-async';
/**
 * Legacy app logout page, joined to `environment.OLD_NEW_URL` by {@link legacyAppUrl}.
 * Kept in one place so a future endpoint change is a single edit; hitting it ends the
 * ASP.NET session server-side (rather than the login bounce).
 */
const LOGOUT_PAGE_PATH = '/logout.aspx';

/**
 * Centralizes application logout so every trigger (the header profile menu today,
 * an idle-timeout or a global "log out everywhere" tomorrow) tears the session
 * down the same way and lands on the same endpoint.
 *
 * Three concerns, all in the SAME TICK as the click:
 * 1. **Server-side expiry** — a fire-and-forget, authenticated POST to
 *    {@link TOKEN_EXPIRE_PATH} so the token is invalidated on the backend, not just
 *    dropped on the client. See {@link expireTokenServerSide} for why this is a raw
 *    `fetch` with `keepalive` rather than `ApiService`.
 * 2. **State teardown** — `UserService.resetUserServiceState()` invalidates the
 *    in-memory auth epoch / token / credential vault / caches, then `clearSession()`
 *    wipes the whole sessionStorage bag so no profile, role or filter state leaks
 *    into the next login.
 * 3. **Redirect** — branches by environment: in development, navigates in-app to
 *    `/token-login` (no legacy session to end); in production, redirects to the
 *    external legacy logout page (`legacyAppUrl(LOGOUT_PAGE_PATH)`, which ends the
 *    ASP.NET session server-side).
 *
 * WHY nothing is awaited: logout used to run the teardown + redirect inside the
 * token-expire response's `finalize`, so the user sat on the app for the whole
 * round-trip (1–2s observed on UAT) after clicking Logout — a window long enough for
 * the app to render an intermediate page before the browser left. Nothing about the
 * teardown or the redirect depends on the server's answer: a failed expiry was
 * already non-fatal (the old code redirected anyway). So the request is dispatched
 * and the logout completes immediately, making it instant.
 *
 * Browser-only for the request + redirect: both read `window`, which does not exist
 * during the SSR render pass, so they no-op on the server (state teardown is still
 * safe there because `UserService` guards its own storage).
 */
@Injectable({ providedIn: 'root' })
export class LogoutService {

  /** Base URL for the logout/token-expire API call (Compliance backend). */
  public apiBaseUrl = environment.Compliance_API_URL;
  /** Owns the in-memory auth state + session storage torn down on logout. */
  private readonly userService = inject(UserService);
  /** Platform token — gates the browser-only request + redirect (no `window` on the server). */
  private readonly platformId = inject(PLATFORM_ID);
  /** In-app navigation for the dev-only post-logout redirect to token-login. */
  private readonly router = inject(Router);

  /**
   * Expires the session token server-side, clears all app/session state and redirects
   * to the logout endpoint — all synchronously, so the browser leaves the app on the
   * click with no intermediate render.
   * @returns {void}
   */
  logout(): void {
    this.expireTokenServerSide();
    this.finishLogout();
  }

  /**
   * Dispatches the authenticated, HMAC-signed token-expire POST without waiting for
   * it. Best-effort by design — the local teardown and the redirect run regardless.
   *
   * WHY a raw `fetch` instead of `ApiService.postEmpty`:
   * - **`keepalive: true`.** We unload the document in the same tick, and the browser
   *   aborts in-flight `fetch`/XHR on unload — the token would then only be dropped on
   *   the client, never expired on the server. A keepalive request is guaranteed to
   *   outlive the page. Angular's `HttpClient` (even with `withFetch()`) exposes no way
   *   to set it.
   * - **Synchronous dispatch.** `ApiService` resolves the token through
   *   `ensureValidToken()`, an Observable; its refresh branch would land *after* the
   *   teardown had already cleared the credentials it signs with. {@link
   *   UserService.getValidToken} is that method's fast path, without the refresh.
   *
   * The headers mirror `ApiService.postEmpty` exactly (bearer + payload-less HMAC over
   * an empty message, `{}` body). No stored token, or no verifier to sign with, means
   * there is nothing to expire — skip the call and let the logout proceed.
   */
  private expireTokenServerSide(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const token = this.userService.getValidToken();
    if (!token) {
      return;
    }

    let hmac: string;
    try {
      hmac = this.userService.generateHmacWithoutPayload('');
    } catch {
      // No verifier in session → the request could not be signed anyway.
      return;
    }

    // Errors are swallowed, not surfaced: a dead token or a network failure must never
    // strand the user in a logged-in-looking UI. `void` marks the promise as unawaited.
    void fetch(`${this.apiBaseUrl}${TOKEN_EXPIRE_PATH}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Payload-HMAC': hmac,
      },
      body: '{}',
    }).catch(() => { /* best-effort: the client-side teardown below is what matters */ });
  }

  /** Clears local state, then redirects by environment: the dev token-entry
   * page in-app for local builds (no legacy session to end), the external
   * legacy logout page in production (ends the ASP.NET session server-side). */
  private finishLogout(): void {

    this.userService.resetUserServiceState();
    this.userService.clearSession();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Dev has no legacy ASP.NET session to end and needs the token-entry page to
    // sign back in — mirror the dev/prod split the root route and
    // SessionExpiryService already use.
    if (!environment.production) {
      this.router.navigateByUrl(TOKEN_LOGIN_PATH);
      return;
    }

    // Prod: land on the legacy logout page (built off OLD_NEW_URL, the same base
    // AuthRedirectComponent appends to) so the ASP.NET session ends server-side.
    window.location.href = legacyAppUrl(LOGOUT_PAGE_PATH);
  }
}
